import React from 'react';
import { TypeBRenderer } from '@/src/modules/renderer/TypeBRenderer';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { transliterateToMarathi } from '@/src/infrastructure/lib/transliterate';
import { extractCoupleNames, parseFlexibleDate } from '@/src/infrastructure/lib/weddingDataHelper';
import manifest from './manifest.json';

const META: TemplateMetadata = {
  id: 'marathi-wedding-classic',
  name: 'Marathi Wedding Classic',
  occasionType: 'WEDDING',
  description: 'A beautiful traditional Marathi wedding template generated with Type B pipeline.',
  isPremium: true,
  tags: ['marathi', 'traditional', 'type-b'],
  colorScheme: { primary: '#D32F2F', accent: '#FFB300', bg: '#FFFDE7' },
};

export function MarathiWeddingClassic({ data }: { data: InvitationData }) {
  const groom = data.customData?.wedding?.groom;
  const bride = data.customData?.wedding?.bride;
  const parsedCouple = extractCoupleNames(data.eventName);

  const rawBrideName = bride?.fullName || bride?.nickname || parsedCouple?.bride;
  const rawGroomName = groom?.fullName || groom?.nickname || parsedCouple?.groom;

  const brideFather = bride?.fatherName ? transliterateToMarathi(bride.fatherName) : '';
  const brideMother = bride?.motherName ? transliterateToMarathi(bride.motherName) : '';
  const brideParents = bride?.parents
    ? `${transliterateToMarathi(bride.parents)}\nयांची कन्या`
    : brideFather
    ? `सौ. ${brideMother} व श्री. ${brideFather}\nयांची कन्या`
    : data.hostName
    ? `निमंत्रक: ${transliterateToMarathi(data.hostName)}\nव समस्त परिवार`
    : '';

  const groomFather = groom?.fatherName ? transliterateToMarathi(groom.fatherName) : '';
  const groomMother = groom?.motherName ? transliterateToMarathi(groom.motherName) : '';
  const groomParents = groom?.parents
    ? `${transliterateToMarathi(groom.parents)}\nयांचे चिरंजीव`
    : groomFather
    ? `सौ. ${groomMother} व श्री. ${groomFather}\nयांचे चिरंजीव`
    : data.hostName
    ? `निमंत्रक: ${transliterateToMarathi(data.hostName)}\nव समस्त परिवार`
    : '';

  const brideName = rawBrideName ? `चि.सौ.कां. ${transliterateToMarathi(rawBrideName)}` : 'चि.सौ.कां. वधू';
  const groomName = rawGroomName ? `चि. ${transliterateToMarathi(rawGroomName)}` : 'चि. वर';

  const rawCeremonies = data.customData?.wedding?.ceremonies || data.customData?.wedding?.weddingEvents;
  const weddingEvent: any = rawCeremonies?.find(
    (e: any) => {
      const title = (e.name || e.title || '').toLowerCase();
      return title.includes('wedding') || title.includes('विवाह');
    }
  );
  const eventTime = weddingEvent?.time || weddingEvent?.startTime || 'दुपारी 12:45 मि';

  const parsedEventDate = parseFlexibleDate(data.eventDate);
  let formattedDate = 'शुभविवाह दिनांक लवकरच जाहीर होईल';
  if (parsedEventDate) {
    const d = parsedEventDate;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const weekdays = ['रविवार', 'सोमवार', 'मंगळवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'];
    const weekdayName = weekdays[d.getDay()];

    let timeStr = eventTime;
    if (timeStr.toLowerCase().includes('pm')) {
      timeStr = timeStr.replace(/pm/i, 'वाजता');
    } else if (timeStr.toLowerCase().includes('am')) {
      timeStr = timeStr.replace(/am/i, 'वाजता');
    }
    timeStr = transliterateToMarathi(timeStr);

    formattedDate = `${weekdayName} दि. ${day}/${month}/${year}\nरोजी ${timeStr}`;
  }

  const haldiEvent: any = rawCeremonies?.find(
    (e: any) => {
      const title = (e.name || e.title || '').toLowerCase();
      return (
        title.includes('haldi') ||
        title.includes('हळद') ||
        title.includes('reception') ||
        title.includes('स्वागत')
      );
    }
  );
  const receptionTitle = haldiEvent ? transliterateToMarathi(haldiEvent.name || haldiEvent.title) : 'हळद समारंभ';
  const haldiEventDate = parseFlexibleDate(haldiEvent?.date) || (parsedEventDate ? new Date(parsedEventDate.getTime() - 86400000) : null);
  let receptionDate = 'हळद सोहळा लवकरच';
  if (haldiEventDate) {
    const d = haldiEventDate;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const weekdays = ['रविवार', 'सोमवार', 'मंगळवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'];
    const weekdayName = weekdays[d.getDay()];
    const timeStr = haldiEvent?.startTime ? transliterateToMarathi(haldiEvent.startTime) : 'सायं. 06.00 वा.';
    receptionDate = `${weekdayName} दि. ${day}/${month}/${year} ${timeStr}`;
  }

  const venueAddress = data.eventVenue ? transliterateToMarathi(data.eventVenue) : 'मंगल कार्यालय';
  const familyNote = data.hostName
    ? `निमंत्रक\n${transliterateToMarathi(data.hostName)}\nसमस्त परिवार`
    : 'निमंत्रक\nसमस्त परिवार';

  // Ensure Ganesha shloka is always present at the top
  // Use eventName as fallback if customMessage is not provided
  const coupleNames = `|| श्री गणेशाय नमः ||\n${
    data.customMessage && data.customMessage !== 'A heartfelt message to your guests goes here.'
      ? transliterateToMarathi(data.customMessage)
      : data.eventName
      ? transliterateToMarathi(data.eventName)
      : 'स.न.वि.वि आमच्या येथे श्रीहरी कृपेने'
  }`;

  const textValues = {
    coupleNames,
    bride_name: brideName,
    bride_details: brideParents,
    groom_name: groomName,
    groom_details: groomParents,
    wedding_title: 'यांचा शुभविवाह समारंभ',
    wedding_date: formattedDate,
    wedding_note: 'या शुभ मुहूर्तावर करण्याचे योजिले आहे',
    reception_title: receptionTitle,
    reception_date: receptionDate,
    ceremony_title: 'विवाहस्थळ',
    venue_address: venueAddress,
    family_note: familyNote,
  };

  // Modify manifest in-memory to hide gibberish layers and fix text slot layouts
  const modifiedManifest = React.useMemo(() => {
    // 1. Filter out layers containing pre-baked gibberish English text
    const filteredLayers = manifest.layers.filter(
      (l) => l.id !== 'wedding-text-block' && l.id !== 'event-details' && l.id !== 'decorative-elements'
    );

    // 2. Map of optimized coordinates and font sizes for a clean vertical flow layout (1080x1920 canvas)
    const slotConfigs: Record<string, { y: number; fontSize: number; fontWeight?: string; color?: string }> = {
      coupleNames: { y: 15, fontSize: 32, fontWeight: 'normal' },
      bride_name: { y: 24, fontSize: 44, fontWeight: 'bold' },
      bride_details: { y: 29, fontSize: 26 },
      groom_name: { y: 36, fontSize: 44, fontWeight: 'bold' },
      groom_details: { y: 41, fontSize: 26 },
      wedding_title: { y: 48, fontSize: 32 },
      wedding_date: { y: 55, fontSize: 28, fontWeight: 'bold', color: '#D32F2F' },
      wedding_note: { y: 60, fontSize: 24 },
      reception_title: { y: 66, fontSize: 28, fontWeight: 'bold' },
      reception_date: { y: 70, fontSize: 26 },
      ceremony_title: { y: 76, fontSize: 28, fontWeight: 'bold' },
      venue_address: { y: 80, fontSize: 26 },
      family_note: { y: 87, fontSize: 24 },
    };

    const updatedTextSlots = manifest.textSlots.map((slot) => {
      const config = slotConfigs[slot.key];
      if (config) {
        return {
          ...slot,
          y: config.y,
          fontSize: config.fontSize,
          fontWeight: config.fontWeight || slot.fontWeight,
          color: config.color || slot.color,
        };
      }
      return slot;
    });

    return {
      ...manifest,
      layers: filteredLayers,
      textSlots: updatedTextSlots,
    };
  }, []);

  return (
    <TypeBRenderer
      manifest={modifiedManifest as any}
      textValues={textValues}
      className="bg-[#FFFDE7]"
    />
  );
}

registry.register({ metadata: META, component: MarathiWeddingClassic });
